// Function definitions and command parsing. A function is a mnemonic
// (`DES`, `GP`) with a `run` that renders into a panel body.

import { resolve } from "./securities";

export type Group = "Equity" | "Markets" | "News & Research" | "Rates & Economy" | "Tools";

export interface PanelApi {
  /// Run a command in this panel, as if typed.
  go(cmd: string): void;
  /// Run a command in the next panel (shift-click).
  goOther(cmd: string): void;
  /// Register a numbered action ("3 <GO>"); returns its number. Pass `at`
  /// to replace an existing number when re-rendering.
  action(cmd: string | (() => void) | null, at?: number): number;
  /// Status line: source and age of the data on screen.
  status(text: string, meta?: { asof?: number; stale?: boolean }): void;
  /// Call `fn` every `ms` while the function is mounted and the tab visible.
  every(ms: number, fn: () => unknown): void;
}

export interface Ctx {
  /// Loaded security as a Yahoo symbol, if any.
  sec: string | null;
  /// Remaining tokens after the function code, upper-cased.
  args: string[];
  body: HTMLElement;
  /// Aborted when the function is replaced; pass to fetches.
  signal: AbortSignal;
  panel: PanelApi;
}

export interface FnDef {
  code: string;
  name: string;
  group: Group;
  /// Whether the function operates on a loaded security.
  needsSec: boolean;
  /// Tokens after the code that are arguments rather than a security.
  args?: RegExp;
  help: string;
  aliases?: string[];
  run(ctx: Ctx): void | Promise<void>;
}

const registry = new Map<string, FnDef>();

export function register(...defs: FnDef[]): void {
  for (const d of defs) {
    registry.set(d.code, d);
    for (const a of d.aliases ?? []) registry.set(a, d);
  }
}

export function lookup(code: string): FnDef | undefined {
  return registry.get(code.toUpperCase());
}

export function allFns(): FnDef[] {
  return [...new Set(registry.values())];
}

export type Parsed =
  | { kind: "action"; n: number }
  | { kind: "run"; fn: FnDef; sec: string | null; args: string[] }
  | { kind: "error"; msg: string };

/// Parse `[SECURITY] [FUNCTION] [ARGS]` (or `FUNCTION SECURITY ARGS`).
/// `current` is the panel's loaded security.
export function parse(input: string, current: string | null): Parsed {
  const toks = input.trim().toUpperCase().split(/\s+/).filter(Boolean);
  if (toks.length === 0) return { kind: "error", msg: "" };
  if (toks.length === 1 && /^\d{1,3}$/.test(toks[0])) return { kind: "action", n: Number(toks[0]) };

  const at = toks.findIndex((t) => registry.has(t));
  if (at < 0) {
    const sec = resolve(toks);
    if (!sec) return { kind: "error", msg: `Unknown command: ${input.trim()}` };
    return { kind: "run", fn: registry.get("DES")!, sec, args: [] };
  }

  const fn = registry.get(toks[at])!;
  const before = toks.slice(0, at);
  const after = toks.slice(at + 1);
  const args = after.filter((t) => fn.args?.test(t));
  const rest = after.filter((t) => !fn.args?.test(t));

  let sec: string | null = null;
  if (before.length) sec = resolve(before);
  else if (rest.length && fn.needsSec) sec = resolve(rest);
  else if (rest.length) args.push(...rest);
  if ((before.length || (rest.length && fn.needsSec)) && !sec) {
    return { kind: "error", msg: `Not a security: ${(before.length ? before : rest).join(" ")}` };
  }
  sec ??= current;
  if (fn.needsSec && !sec) {
    return { kind: "error", msg: `${fn.code} needs a security, e.g. AAPL ${fn.code} <GO>` };
  }
  return { kind: "run", fn, sec, args };
}
